import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { captureException } from "@sentry/react-native";

import useTransactionDetailsAPI from "./TransactionDetailsAPIContext";
import { TransactionOrWithdrawal } from "../types";

type ContextType = {
  isLoading: boolean;
  transaction: TransactionOrWithdrawal;
  fetchTransaction: () => Promise<void>;
  selectTransactionUuid: (uuid: string) => void;
};

const Context = createContext<ContextType | null>(null);

export default function useTransaction() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("Missing TransactionContextProvider");
  return ctx;
}

type ProviderProps = PropsWithChildren<{ uuid: string | null }>;
export function TransactionContextProvider(props: ProviderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [transaction, setTransaction] = useState<TransactionOrWithdrawal>(null);
  const [uuid, selectTransactionUuid] = useState(props.uuid);

  const { getTransaction, getTransactionSummary, patchTransaction } =
    useTransactionDetailsAPI();

  const fetchTransaction = async (retryCount = 0) => {
    if (!uuid) return;

    try {
      setIsLoading(true);
      const txn = await getTransaction(uuid);
      const summary = await getTransactionSummary(uuid);
      await patchTransaction(summary);
      setTransaction(txn);
    } catch (e) {
      if (retryCount < 3) {
        fetchTransaction(retryCount + 1);
      } else {
        captureException(e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (uuid) fetchTransaction();

    // reset to ensure we dont show stale data / flickering when navigating between txns
    return () => setTransaction(null);
  }, [uuid]);

  return (
    <Context.Provider
      value={{
        isLoading,
        transaction,
        fetchTransaction,
        selectTransactionUuid,
      }}
    >
      {props.children}
    </Context.Provider>
  );
}
